import addMonths from "date-fns/addMonths";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import ja from "date-fns/locale/ja";
import parse from "date-fns/parse";

import { zeroPad } from "./common-utils";

export const formatJpDate = (date?: string | null) => {
  if (!date) return "";
  const dt = new Date(date);
  return dt ? `${dt.getFullYear()}年${zeroPad(dt.getMonth() + 1, 2)}月${zeroPad(dt.getDate(), 2)}日` : "";
};
export const formatJpDateForNumber = (date?: number | null) => {
  if (!date) return "";
  const dt = new Date(date * 1000);
  return dt ? `${dt.getFullYear()}年${zeroPad(dt.getMonth() + 1, 2)}月${zeroPad(dt.getDate(), 2)}日` : "";
};

export const formatSlashDate = (date?: number | null) => {
  if (!date) return "";
  const dt = new Date(date * 1000);
  return dt ? `${dt.getFullYear()}/${zeroPad(dt.getMonth() + 1, 2)}/${zeroPad(dt.getDate(), 2)}` : "";
};

export const formatSlashDateMonthDay = (date?: number | null) => {
  if (!date) return "";
  const dt = new Date(date * 1000);
  return dt ? `${zeroPad(dt.getMonth() + 1, 2)}/${zeroPad(dt.getDate(), 2)}` : "";
};

export const formatSlashDateStrings = (date?: string | null) => {
  if (!date) return "";
  const dt = new Date(date);
  return dt ? `${dt.getFullYear()}/${zeroPad(dt.getMonth() + 1, 2)}/${zeroPad(dt.getDate(), 2)}` : "";
};

export const formatSlashTime = (date?: number | null) => {
  if (!date) return "";
  const dt = new Date(date * 1000);
  return dt ? `${zeroPad(dt.getHours(), 2)}:${zeroPad(dt.getMinutes(), 2)}` : "";
};
export const formatSlashDateTime = (date?: number | null) => {
  if (!date) return "";
  const dt = new Date(date * 1000);
  return dt
    ? `${dt.getFullYear()}/${zeroPad(dt.getMonth() + 1, 2)}/${zeroPad(dt.getDate(), 2)} ${zeroPad(
        dt.getHours(),
        2,
      )}:${zeroPad(dt.getMinutes(), 2)}`
    : "";
};

export const formatJpDateTime = (date?: number | null) => {
  if (!date) return "";
  const dt = new Date(date * 1000);
  return dt
    ? `${dt.getFullYear()}年${zeroPad(dt.getMonth() + 1, 2)}月${zeroPad(dt.getDate(), 2)}日 ${zeroPad(
        dt.getHours(),
        2,
      )}:${zeroPad(dt.getMinutes(), 2)}`
    : "";
};

/**
 * @deprecated 出力 `yyyy年MM月dd日(E) HH:mm` が要件定義書 §2.2 の表記ルールと4点ずれている
 * （ゼロ埋めの有無 `04月` vs `4月` / 曜日の囲み `(月)` vs `[月]` / 年が必ず付く / 時刻が必ず付く）。
 *
 * **新規実装では使わない。** 曜日つきの日付は {@link formatJpDateWithDay} /
 * {@link formatJpMonthDayWithDay} を使う（#1540 で新設）。
 *
 * 現時点でリポジトリ内の呼び出しは0件だが、`@unit/utility` は3アプリ共有の公開APIのため
 * 互換維持を優先して削除していない。
 */
export const formatJpDateTimeDay = (date?: number | null) => {
  if (!date) return "";
  return format(new Date(date * 1000), "yyyy年MM月dd日(E) HH:mm", { locale: ja });
};

/**
 * ⚠ こちらは**端末ローカルのタイムゾーン**で時刻を出す（既存の呼び出し元があるため挙動を変えない）。
 * 曜日つきの日付（{@link formatJpDateWithDay} 系）は JST 固定なので、
 * 「日付 + 時刻」を同じカードに並べる場合、非JST端末では基準が食い違う。
 * 揃えるかどうかは最初の利用者である P2-2 (#1541) へ引き継ぐ。
 */
export const formatJpDTime = (date?: number | null) => {
  if (!date) return "";
  return format(new Date(date * 1000), "HH:mm", { locale: ja });
};

/**
 * 業務タイムゾーン。来店日・日程は端末のタイムゾーンではなく**サービスの暦日**で決める。
 * （`formatJstDate` と同じ理由。どの端末から開いても同じ日付・同じ曜日になる）
 */
const JST_TIME_ZONE = "Asia/Tokyo";

const jstDateWithDayFormatter = new Intl.DateTimeFormat("ja-JP", {
  timeZone: JST_TIME_ZONE,
  year: "numeric",
  month: "numeric",
  day: "numeric",
  weekday: "short",
});

type JstDateWithDayParts = { year: string; month: string; day: string; weekday: string };

/**
 * エポック秒を JST の年月日＋曜日に分解する。取り出せないときは `null`。
 *
 * - `* 1000` の変換はここで行う（呼び出し側にエポック秒の変換を要求しない）
 * - `0` は有効な日時（1970年1月1日 09:00 JST）として扱う
 * - `null` / `undefined` / 非数 / Date の範囲外は `null`
 */
const toJstDateWithDayParts = (epochSec?: number | null): JstDateWithDayParts | null => {
  if (epochSec === null || epochSec === undefined || !Number.isFinite(epochSec)) return null;
  const date = new Date(epochSec * 1000);
  if (Number.isNaN(date.getTime())) return null;

  const parts = jstDateWithDayFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  // ICU のバージョンによって short が「金」/「金曜日」に振れるため、囲みの中は1文字に正規化する
  const weekday = pick("weekday").replace(/曜日$/, "");
  const picked = { year: pick("year"), month: pick("month"), day: pick("day"), weekday };
  if (!picked.year || !picked.month || !picked.day || !picked.weekday) return null;
  return picked;
};

/**
 * 来店日ナビゲーター（要件定義書 §2.2）: `2026年4月17日 [金]`
 *
 * - 引数は**エポック秒**（`* 1000` は内部で行う）
 * - **ゼロ埋めしない**（`4月17日`）／曜日は**角括弧**／**時刻は付けない**
 * - 暦日と曜日は **JST 固定**
 * - ⚠ 曜日は必ず計算する。要件定義書・Figma の例示 `2026年4月17日 [月]` は誤りで、
 *   2026-04-17 は**金曜日**（誤った曜日を定数で埋め込まない）
 */
export const formatJpDateWithDay = (epochSec?: number | null): string => {
  const parts = toJstDateWithDayParts(epochSec);
  if (!parts) return "";
  return `${parts.year}年${parts.month}月${parts.day}日 [${parts.weekday}]`;
};

/**
 * カード内の来店予定日（要件定義書 §2.2）: `4月17日 [金]`
 *
 * 時刻（`18:00`）は別要素として既存 {@link formatJpDTime} を使う。
 * 仕様・制約は {@link formatJpDateWithDay} と同じ。
 */
export const formatJpMonthDayWithDay = (epochSec?: number | null): string => {
  const parts = toJstDateWithDayParts(epochSec);
  if (!parts) return "";
  return `${parts.month}月${parts.day}日 [${parts.weekday}]`;
};

export const formatApiDate = (date?: Date | null) => {
  if (!date) return "";
  const dt = new Date(date);
  return dt ? `${dt.getFullYear()}-${zeroPad(dt.getMonth() + 1, 2)}-${zeroPad(dt.getDate(), 2)}` : "";
};

export const formatSlashApiDate = (date?: Date | null) => {
  if (!date) return "";
  const dt = new Date(date);
  return dt ? `${dt.getFullYear()}/${zeroPad(dt.getMonth() + 1, 2)}/${zeroPad(dt.getDate(), 2)}` : "";
};

export const formatApiTime = (date?: Date | null) => {
  if (!date) return "";
  const dt = new Date(date);
  return dt ? `${zeroPad(dt.getHours(), 2)}:${zeroPad(dt.getMinutes(), 2)}` : "";
};
export const validateIsExpired = (endDate: string | null) => {
  if (!endDate) return false;
  const targetEndDate = new Date(endDate);
  targetEndDate.setHours(0, 0, 0, 0);
  targetEndDate.setDate(targetEndDate.getDate() + 1);
  return new Date().getTime() > new Date(targetEndDate).getTime();
};

export const formatTimeToDate = (time: string) => {
  const dt = new Date();
  const timeArray = time.split(":");
  dt.setHours(Number(timeArray[0]), Number(timeArray[1]), Number(timeArray[2]), 0);
  return dt;
};

export const formattedDateAndTimeConverter = (date: Date | null, time: Date | null) => {
  if (!date || !time) {
    return 0;
  }
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    time.getMilliseconds(),
  ).getTime();
};
export const addDays = (date: Date | null, days: number) => {
  if (!date) return undefined;
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const compareAvailableTime = (
  targetDatetime: number,
  availableStartTime: string,
  availableEndTime: string | null,
): boolean => {
  const targetDate = new Date(targetDatetime * 1000);
  const startTime = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate(),
    ...availableStartTime.split(":").map((x) => parseInt(x, 10)),
  );
  if (!availableEndTime) {
    return startTime <= targetDate;
  }
  const endTime = new Date(
    targetDate.getFullYear(),
    targetDate.getMonth(),
    targetDate.getDate(),
    ...availableEndTime.split(":").map((x) => parseInt(x, 10)),
  );
  return startTime <= targetDate && targetDate <= endTime;
};

export const calculateTimeDiff = (createdAt: number) => {
  const actionDate = new Date(createdAt * 1000);
  const currentDate = new Date();
  const diff = currentDate.getTime() > actionDate.getTime() ? currentDate.getTime() - actionDate.getTime() : 0;
  const timeDiff = new Date(diff);

  if (timeDiff.getUTCFullYear() - 1970 > 0) {
    return `${actionDate.getFullYear()}/${zeroPad(actionDate.getMonth() + 1, 2)}/${zeroPad(actionDate.getDate(), 2)}`;
  }
  if (timeDiff.getUTCMonth() > 0) {
    const diffMonth = timeDiff.getUTCMonth();

    return `${diffMonth}ヶ月前`;
  }
  if (timeDiff.getUTCDate() - 1 > 0) {
    const diffDay = timeDiff.getUTCDate() - 1;

    return `${diffDay}日前`;
  }
  if (timeDiff.getUTCHours() > 0) {
    const diffHours = timeDiff.getUTCHours();

    return `${diffHours}時間前`;
  }
  if (timeDiff.getUTCMinutes() > 0) {
    const diffMinutes = timeDiff.getUTCMinutes();

    return `${diffMinutes}分前`;
  }
  if (timeDiff.getUTCSeconds() > 10) {
    const diffSeconds = timeDiff.getUTCSeconds();

    return `${diffSeconds}秒前`;
  }
  if (timeDiff.getUTCSeconds() <= 10) {
    return "たった今";
  }
};

export const formatJpChartXAxis = (date?: string | null) => {
  if (!date) return "";
  const dt = new Date(date);
  return dt ? `${dt.getMonth() + 1}月${zeroPad(dt.getDate(), 2)}日` : "";
};

export const formatJpCharDate = (date?: string | null) => {
  if (!date) return "";
  const dt = new Date(date);
  return dt ? `${dt.getFullYear()}年${zeroPad(dt.getMonth() + 1, 2)}月${zeroPad(dt.getDate(), 2)}日` : "";
};

/**
 * 引数にnullを取る時に`null`を返す事以外は`new Date(date)`と同じ動作をします。
 * @param date
 * @returns
 */
export const getDateInstance = (date?: string | number | null): Date | null => {
  if (!date) return null;
  return new Date(date);
};

/**
 * 第1引数~第3引数で受け取った時分秒のDateオブジェクトを生成する。
 * 日付については第4引数の`date`が反映される。
 * @param hours
 * @param minutes
 * @param seconds
 * @param date
 * @returns
 */
export const datetime = (hours: number, minutes: number, seconds: number, date: Date = new Date()) => {
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  return date;
};

export const formatDateRange = (dateRange: [Date, Date]) => {
  const start = dateRange[0];
  const end = dateRange[1];
  const startMonth = start.getMonth() + 1;
  const startDate = start.getDate();
  const endMonth = end.getMonth() + 1;
  const endDate = end.getDate();
  return `${start.getFullYear()}/${startMonth < 10 ? "0" + startMonth : startMonth}/${
    startDate < 10 ? "0" + startDate : startDate
  }-${end.getFullYear()}/${endMonth < 10 ? "0" + endMonth : endMonth}/${endDate < 10 ? "0" + endDate : endDate}`;
};

export const formatDateYYYYMMDD = (date: Date) => {
  return `${date.getFullYear()}-${zeroPad(date.getMonth() + 1, 2)}-${zeroPad(date.getDate(), 2)}`;
};

const jstDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * JST（サービスの暦日）での yyyy-MM-dd。
 * API / Firestore の日別キーは JST の暦日なので、集計・突合はこの形式に揃える。
 * 端末のタイムゾーン依存の `formatDateYYYYMMDD` とは異なり、どこから開いても同じ日付になる。
 */
export const formatJstDate = (date: Date): string => {
  // format() の出力書式は ICU のバージョンに依存しうるため、パーツから明示的に組み立てる
  const parts = jstDateFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return `${pick("year")}-${pick("month")}-${pick("day")}`;
};

/** JST の暦日（yyyy-MM-dd）を、その日の JST 0 時を指す Date に変換する */
export const parseJstDate = (jstDate: string): Date => new Date(`${jstDate}T00:00:00+09:00`);

/** JST の暦日（yyyy-MM-dd）を日数分ずらす。JST は夏時間が無いため単純な加算でよい */
export const addJstDays = (jstDate: string, days: number): string =>
  formatJstDate(new Date(parseJstDate(jstDate).getTime() + days * MILLISECONDS_PER_DAY));

/** JST の暦日（yyyy-MM-dd）同士の日数差（`to` - `from`）。JST は夏時間が無いため単純な割り算でよい */
export const differenceInJstDays = (from: string, to: string): number =>
  Math.round((parseJstDate(to).getTime() - parseJstDate(from).getTime()) / MILLISECONDS_PER_DAY);

/**
 * Compares yyyy-MM-dd formatted date strings: `date < compareTo`.
 * Falls back to current JST date when `compareTo` is omitted.
 * @param monthsOffset If set (non-zero), shifts `date` forward by that many calendar months before comparing.
 */
export function isDateBefore(date: string | null | undefined, compareTo?: Date, monthsOffset?: number): boolean {
  if (!date) return false;

  let parsedDate = parse(date.split("T")[0], "yyyy-MM-dd", new Date());
  if (!isValid(parsedDate)) return false;
  if (monthsOffset) parsedDate = addMonths(parsedDate, monthsOffset);

  const dateStr = format(parsedDate, "yyyy-MM-dd");
  const compareToStr = jstDateFormatter.format(compareTo ?? new Date());

  return dateStr < compareToStr;
}
